import { useState } from "react";
import {
  Phone, MessageCircle, Mail, Globe, Search, Play, UserCheck, PauseCircle,
  UserPlus, CheckCircle2, Send, Save, PhoneCall, Sparkles, StickyNote,
} from "lucide-react";
import DashLayout from "@/components/dashboard/DashLayout";
import { StatusBadge } from "@/components/brand";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const CHANNELS = {
  call: { icon: Phone, label: "Call", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  whatsapp: { icon: MessageCircle, label: "WhatsApp", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  email: { icon: Mail, label: "Email", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  website: { icon: Globe, label: "Website", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

function ChannelChip({ channel }) {
  const c = CHANNELS[channel];
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold ${c.cls}`} data-testid={`channel-chip-${channel}`}>
      <c.icon className="h-3 w-3" /> {c.label}
    </span>
  );
}

const CONVERSATIONS = [
  { id: 0, name: "Ramesh Kumar", channel: "call", last: "Asked for AC prices, placed order", time: "9:47 AM", status: "Resolved", type: "call" },
  { id: 1, name: "Priya Sharma", channel: "whatsapp", last: "My mixer grinder arrived damaged…", time: "10:02 AM", status: "Needs you", type: "chat" },
  { id: 2, name: "Amit Verma", channel: "whatsapp", last: "Where is my order?", time: "10:58 AM", status: "Active", type: "chat" },
  { id: 3, name: "Hotel Sunrise", channel: "email", last: "Bulk enquiry: 20 air conditioners", time: "11:20 AM", status: "Needs you", type: "chat" },
  { id: 4, name: "Neha Gupta", channel: "website", last: "Do you deliver to Jaipur?", time: "12:05 PM", status: "Active", type: "chat" },
  { id: 5, name: "Suresh Patel", channel: "call", last: "Asked for discount on refrigerator", time: "12:40 PM", status: "Active", type: "call" },
];

const THREADS = {
  chat: [
    { from: "customer", text: "Hi, where is my order? It was supposed to come yesterday.", time: "10:55 AM" },
    { from: "ai", text: "Namaste Amit ji! Sorry for the wait. I have checked — your order #1042 is out for delivery and will reach you today before 6 PM. Here is your tracking link: track.sharmaelectronics.in/1042", time: "10:55 AM" },
    { from: "customer", text: "Oh great, thank you!", time: "10:57 AM" },
    { from: "ai", text: "You are welcome! Is there anything else I can help you with?", time: "10:57 AM" },
  ],
  call: {
    duration: "2:14",
    summary: [
      "Customer asked for prices of 1.5-ton split ACs",
      "Shared Samsung and LG options with installation details",
      "Customer selected Samsung AC, quantity: 1",
      "Order #1047 created, delivery booked for Saturday",
      "Follow-up: confirm installation time on Friday evening",
    ],
    requirement: "1.5-ton split AC with free installation",
    products: [{ name: "Samsung 1.5-ton Split AC", qty: 1, price: "₹38,500" }],
    followUp: "Call Friday 6 PM to confirm installation slot",
  },
};

const AI_SECTIONS = [
  { key: "business-info", title: "Business information", hint: "Address, timings, delivery areas, payment options", value: "Sharma Electronics, MG Road, Jaipur. Open 10 AM – 9 PM, closed Sundays. Free delivery within 15 km. UPI, cards and cash accepted." },
  { key: "products", title: "Products and services", hint: "What you sell, with prices", value: "ACs, refrigerators, washing machines, kitchen appliances. Installation free on ACs. 1-year store warranty on all products." },
  { key: "faqs", title: "Frequently asked questions", hint: "Common questions and your answers", value: "EMI available on orders above ₹10,000. Exchange offers on old appliances. GST invoice provided with every order." },
  { key: "reply-style", title: "Reply style", hint: "How your AI should talk", value: "Polite and friendly. Use 'Namaste' as greeting. Short sentences. Hindi or English — match the customer's language." },
  { key: "never-say", title: "Things AI must never say or do", hint: "Strict limits", value: "Never promise discounts above 10%. Never share supplier details. Never argue with a customer." },
  { key: "handover", title: "When to transfer to a human", hint: "When the AI should call you in", value: "Refund requests, complaints about damaged items, orders above ₹50,000, or if the customer asks for the owner." },
];

export default function Inbox() {
  const [selected, setSelected] = useState(2);
  const [filter, setFilter] = useState("all");
  const [confirm, setConfirm] = useState(null);
  const convo = CONVERSATIONS[selected];

  const filtered = CONVERSATIONS.filter((c) => filter === "all" || c.channel === filter);

  const doAction = () => {
    const msgs = {
      takeover: "You are now handling this conversation yourself",
      pause: "AI paused for this customer",
      assign: "Conversation assigned to team member",
      resolve: "Conversation marked resolved",
    };
    toast.success(`Design preview: ${msgs[confirm]}`);
    setConfirm(null);
  };

  return (
    <DashLayout title="Conversations" subtitle="">
      <div data-testid="inbox-page">
        {/* Top actions */}
        <div className="flex flex-wrap items-center justify-between gap-3" data-testid="inbox-top-actions">
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900">Unified Inbox</h1>
          <div className="flex flex-wrap gap-2">
            {[
              ["takeover", "Take over conversation", UserCheck, "bg-slate-900 text-white hover:bg-slate-800"],
              ["pause", "Pause AI for this customer", PauseCircle, "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"],
              ["assign", "Assign to team member", UserPlus, "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"],
              ["resolve", "Mark resolved", CheckCircle2, "bg-emerald-600 text-white hover:bg-emerald-700"],
            ].map(([key, label, Icon, cls]) => (
              <button
                key={key}
                data-testid={`inbox-action-${key}`}
                onClick={() => setConfirm(key)}
                className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-xs font-semibold transition-colors sm:text-sm ${cls}`}
              >
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 grid gap-5 lg:grid-cols-[300px_1fr] xl:grid-cols-[300px_1fr_320px]">
          {/* LEFT: conversation list */}
          <section className="rounded-2xl border border-slate-100 bg-white" data-testid="conversation-list-panel">
            <div className="border-b border-slate-100 p-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  data-testid="conversation-search-input"
                  placeholder="Search customers"
                  className="w-full rounded-xl border border-slate-200 py-2.5 pl-9 pr-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid="channel-filters">
                {["all", "call", "whatsapp", "email", "website"].map((f) => (
                  <button
                    key={f}
                    data-testid={`filter-${f}`}
                    onClick={() => setFilter(f)}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold capitalize transition-colors ${
                      filter === f ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {f === "all" ? "All" : CHANNELS[f].label}
                  </button>
                ))}
              </div>
            </div>
            <ul className="max-h-[480px] divide-y divide-slate-50 overflow-y-auto scrollbar-thin" data-testid="conversation-list">
              {filtered.map((c) => (
                <li key={c.id}>
                  <button
                    data-testid={`conversation-item-${c.id}`}
                    onClick={() => setSelected(c.id)}
                    className={`w-full px-4 py-3.5 text-left transition-colors ${selected === c.id ? "bg-orange-50/70" : "hover:bg-slate-50"}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-bold text-slate-900">{c.name}</span>
                      <span className="shrink-0 text-[11px] text-slate-400">{c.time}</span>
                    </div>
                    <div className="mt-1.5 flex items-center justify-between gap-2">
                      <span className="truncate text-xs text-slate-500">{c.last}</span>
                      <ChannelChip channel={c.channel} />
                    </div>
                    <div className="mt-1.5">
                      <StatusBadge label={c.status} />
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          </section>

          {/* MIDDLE: thread */}
          <section className="flex flex-col rounded-2xl border border-slate-100 bg-white" data-testid="conversation-thread-panel">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <div>
                <p className="font-display text-base font-bold text-slate-900" data-testid="thread-customer-name">{convo.name}</p>
                <div className="mt-1 flex items-center gap-2">
                  <ChannelChip channel={convo.channel} />
                  <StatusBadge label={convo.status} />
                </div>
              </div>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto bg-[#FAFAF7] p-5 scrollbar-thin" style={{ minHeight: 380 }}>
              {convo.type === "call" ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="call-summary-card">
                  <div className="flex items-center justify-between">
                    <p className="font-display text-base font-bold text-slate-900">Call summary</p>
                    <span className="text-xs font-medium text-slate-400">Duration {THREADS.call.duration}</span>
                  </div>
                  <button
                    data-testid="call-recording-player"
                    onClick={() => toast.info("Design preview: call recording plays here")}
                    className="mt-4 flex w-full items-center gap-3 rounded-xl bg-slate-50 p-3 text-left hover:bg-slate-100"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-orange-600 text-white">
                      <Play className="ml-0.5 h-4 w-4" />
                    </span>
                    <span className="flex-1">
                      <span className="block text-xs font-semibold text-slate-700">Call recording</span>
                      <span className="mt-1.5 flex items-end gap-[3px]" aria-hidden>
                        {[6, 12, 8, 16, 10, 18, 7, 14, 9, 16, 5, 12, 15, 8, 11, 6, 14, 10, 16, 8, 12, 5, 9, 13].map((h, i) => (
                          <span key={i} className="w-[3px] rounded-full bg-orange-400" style={{ height: h }} />
                        ))}
                      </span>
                    </span>
                    <span className="text-xs font-medium text-slate-400">{THREADS.call.duration}</span>
                  </button>
                  <ul className="mt-4 space-y-2">
                    {THREADS.call.summary.map((s, i) => (
                      <li key={i} className="flex gap-2.5 text-sm text-slate-700">
                        <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-orange-500" /> {s}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 grid gap-3 rounded-xl bg-slate-50 p-4 text-sm sm:grid-cols-2">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Customer requirement</p>
                      <p className="mt-1 font-medium text-slate-800">{THREADS.call.requirement}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Follow-up action</p>
                      <p className="mt-1 font-medium text-slate-800">{THREADS.call.followUp}</p>
                    </div>
                  </div>
                  <div className="mt-3 rounded-xl border border-slate-100" data-testid="call-products-selected">
                    <div className="border-b border-slate-100 bg-slate-50/60 px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Products selected
                    </div>
                    {THREADS.call.products.map((p, i) => (
                      <div key={i} className="flex items-center justify-between px-4 py-3 text-sm">
                        <span className="font-medium text-slate-800">{p.name}</span>
                        <span className="text-slate-500">Qty {p.qty} · <span className="font-semibold text-slate-800">{p.price}</span></span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                THREADS.chat.map((m, i) => (
                  <div key={i} className={`flex ${m.from === "customer" ? "justify-start" : "justify-end"}`} data-testid={`message-${i}`}>
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-3 sm:max-w-[70%] ${
                        m.from === "customer"
                          ? "rounded-tl-sm bg-white text-slate-800 ring-1 ring-slate-200"
                          : "rounded-tr-sm bg-orange-600 text-white"
                      }`}
                    >
                      {m.from === "ai" && (
                        <span className="mb-1 inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide">
                          <Sparkles className="h-2.5 w-2.5" /> AI Employee
                        </span>
                      )}
                      <p className="text-sm leading-relaxed">{m.text}</p>
                      <p className={`mt-1 text-right text-[10px] ${m.from === "customer" ? "text-slate-400" : "text-orange-100"}`}>
                        {m.time}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="border-t border-slate-100 p-4">
              <div className="flex gap-2.5">
                <input
                  data-testid="reply-input"
                  placeholder="Write a reply as the business owner..."
                  className="flex-1 rounded-xl border border-slate-200 px-4 py-3 text-sm outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                />
                <button
                  data-testid="reply-send-button"
                  onClick={() => toast.success("Design preview: reply sent")}
                  className="rounded-xl bg-orange-600 px-5 py-3 text-white transition-colors hover:bg-orange-700"
                  aria-label="Send reply"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
              <p className="mt-2 text-xs text-slate-400">The AI employee is handling this conversation. Your message takes over politely.</p>
            </div>
          </section>

          {/* RIGHT: customer panel */}
          <aside className="space-y-5 xl:block" data-testid="customer-info-panel">
            <section className="rounded-2xl border border-slate-100 bg-white p-5">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-slate-900 font-display text-base font-bold text-white">
                  {convo.name[0]}
                </span>
                <div>
                  <p className="font-display text-base font-bold text-slate-900">{convo.name}</p>
                  <p className="text-xs text-slate-500">Customer since 2024</p>
                </div>
              </div>
              <dl className="mt-5 space-y-3 text-sm">
                <div className="flex justify-between gap-3"><dt className="text-slate-500">Phone</dt><dd className="font-medium text-slate-800">+91 98XXX XX210</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slate-500">Email</dt><dd className="truncate font-medium text-slate-800">amit.v@example.com</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slate-500">Current order</dt><dd className="font-medium text-slate-800">#1042 — Mixer grinder</dd></div>
                <div className="flex items-center justify-between gap-3"><dt className="text-slate-500">Tracking status</dt><dd><StatusBadge label="Out for delivery" /></dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slate-500">Previous conversations</dt><dd className="font-medium text-slate-800">6</dd></div>
                <div className="flex justify-between gap-3"><dt className="text-slate-500">Follow-up date</dt><dd className="font-medium text-orange-700">Tomorrow, 11 AM</dd></div>
              </dl>
              <div className="mt-5">
                <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  <StickyNote className="h-3.5 w-3.5" /> Notes
                </p>
                <textarea
                  data-testid="customer-notes-input"
                  rows={3}
                  defaultValue="Prefers Hindi. Interested in exchanging his old refrigerator next month."
                  className="mt-2 w-full rounded-xl border border-slate-200 p-3 text-sm text-slate-700 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-200"
                />
              </div>
            </section>
          </aside>
        </div>

        {/* AI instruction area */}
        <section className="mt-8" data-testid="ai-instructions-section">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="font-display text-xl font-bold text-slate-900">What your AI employee knows</h2>
              <p className="text-sm text-slate-500">Simple cards — edit any time, no technical setup needed.</p>
            </div>
            <button
              data-testid="save-ai-instructions-button"
              onClick={() => toast.success("Design preview: AI instructions saved")}
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
            >
              <Save className="h-4 w-4" /> Save changes
            </button>
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {AI_SECTIONS.map((s) => (
              <div key={s.key} className="rounded-2xl border border-slate-100 bg-white p-5" data-testid={`ai-section-${s.key}`}>
                <p className="font-display text-sm font-bold text-slate-900">{s.title}</p>
                <p className="mt-0.5 text-xs text-slate-400">{s.hint}</p>
                <textarea
                  data-testid={`ai-section-input-${s.key}`}
                  rows={4}
                  defaultValue={s.value}
                  className="mt-3 w-full rounded-xl border border-slate-200 bg-[#FAFAF7] p-3 text-sm leading-relaxed text-slate-700 outline-none focus:border-orange-500 focus:bg-white focus:ring-2 focus:ring-orange-200"
                />
              </div>
            ))}
          </div>
        </section>
      </div>

      <AlertDialog open={!!confirm} onOpenChange={() => setConfirm(null)}>
        <AlertDialogContent data-testid="inbox-action-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">
              {confirm === "takeover" && "Take over this conversation?"}
              {confirm === "pause" && "Pause AI for this customer?"}
              {confirm === "assign" && "Assign to a team member?"}
              {confirm === "resolve" && "Mark this conversation as resolved?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "takeover" && "The AI will politely step back and you will reply to the customer yourself."}
              {confirm === "pause" && "The AI will stop replying to this customer until you turn it back on."}
              {confirm === "assign" && "Choose a team member to handle this conversation."}
              {confirm === "resolve" && "The conversation moves to your history. You can reopen it anytime."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="inbox-action-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="inbox-action-confirm" onClick={doAction}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashLayout>
  );
}
